import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  fetchIncomingConsults,
  clearConsultations,
} from "../store/videoConsult-actions";

interface UseVideoConsultPollingProps {
  enabled: boolean;
  consultantId?: number;
  intervalMs?: number;
}

// Discovery polling with two distinct race guards (the same pattern the
// consult-order lifecycle polls use — busy + AbortController + generation):
//
// 1. Cross-generation staleness (accept race): a poll already in flight when
//    `enabled` flips false (consult accepted, call started) must not resolve
//    later and repopulate `incomingConsults` mid-call. Guarded by a
//    generation counter — the thunk discards settlements whose generation
//    moved — plus an abort of the in-flight request.
// 2. Same-generation reordering: setInterval fires whether or not the
//    previous request resolved, so two overlapping requests could resolve
//    out of order and let a slow OLDER response overwrite NEWER state.
//    Guarded by a single-flight `busy` flag — a tick is skipped while a
//    request is in flight, so requests within a generation never overlap.
//
// `busy` is effect-local (each generation gets a fresh closure), so a dead
// generation's settlement can never clear the new generation's busy state;
// the controller-identity check inside the generation is belt-and-braces.
export const useVideoConsultPolling = ({
  enabled,
  consultantId,
  intervalMs = 10000, // 10 seconds, same as orders
}: UseVideoConsultPollingProps) => {
  const dispatch = useDispatch();
  const { incomingConsults, loadingConsults, consultsFailed } = useSelector(
    (state: RootState) => state.videoConsult
  );

  const generationRef = useRef(0);

  // The current generation's guarded tick, so the manual `startPolling`
  // entry point shares the same single-flight guard as the interval.
  const tickRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!enabled || !consultantId) {
      // Disable = consume the alert data (accept pauses polling; the panel
      // owns the rest of the call) or lost eligibility.
      dispatch(clearConsultations() as any);
      return;
    }

    const generation = ++generationRef.current;
    const isStale = () => generation !== generationRef.current;

    let busy = false;
    let currentController: AbortController | null = null;

    const tick = () => {
      if (busy || isStale()) return;
      busy = true;

      const controller = new AbortController();
      currentController = controller;

      const settled = dispatch(
        fetchIncomingConsults(consultantId, {
          signal: controller.signal,
          isStale,
        }) as any
      );

      Promise.resolve(settled)
        .catch(() => {
          // The thunk handles its own errors; never surface a rejection here.
        })
        .then(() => {
          if (currentController === controller) {
            busy = false;
          }
        });
    };

    tickRef.current = tick;

    // Start immediately
    tick();

    // Set up interval
    const interval = setInterval(tick, intervalMs);

    return () => {
      clearInterval(interval);
      // Intentional: bump the CURRENT generation counter so in-flight
      // settlements from this generation are discarded (plan Step 5b) — this
      // is a mutable counter, not a captured DOM node.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      generationRef.current++;
      currentController?.abort();
      tickRef.current = () => {};
    };
  }, [enabled, consultantId, intervalMs, dispatch]);

  // Cleanup on true unmount only (dispatch identity is stable): the consult
  // list carries caller PII and must not outlive its host.
  useEffect(() => {
    return () => {
      dispatch(clearConsultations() as any);
    };
  }, [dispatch]);

  const startPolling = useCallback(() => {
    tickRef.current();
  }, []);

  // No stopPolling export: the only real stop path is the `enabled` flag
  // (which aborts the in-flight request and bumps the generation). A manual
  // stop function could not honor that contract — the armed interval would
  // refetch within one tick — so exposing one would only mislead callers.
  return {
    incomingConsults,
    loadingConsults,
    consultsFailed,
    startPolling,
  };
};
